import { useLayoutEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { MarkdownText } from "@citrine/atoms/markdown-text";
import type { AgentMessage } from "@citrine/resources/agents";

import { getMessageText } from "#src/components/catalyst/hooks/use-agent-stream";

import * as sx from "./agent-chat-message-list.sx";

export function AgentChatMessageList({
  isStreaming,
  messages,
}: {
  isStreaming: boolean;
  messages: AgentMessage[];
}) {
  const messageListRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const messageList = messageListRef.current;
    if (messageList) {
      messageList.scrollTo({
        behavior: "smooth",
        top: messageList.scrollHeight,
      });
    }
  }, [messages]);

  return (
    <sx.MessageList ref={messageListRef}>
      {messages.length ? null : (
        <sx.WelcomeText>
          <FormattedMessage id="catalyst.agent.question-placeholder" />
        </sx.WelcomeText>
      )}

      {messages.map((message, messageIndex) => {
        const isLastMessage = messageIndex === messages.length - 1;
        const key = `${message.role}-${message.id}`;
        return (
          <sx.MessageBubble key={key} isUser={message.role === "user"}>
            <Message message={message} />
            {message.role === "assistant" && isStreaming && isLastMessage ? (
              <sx.StreamingIndicator />
            ) : null}
          </sx.MessageBubble>
        );
      })}
    </sx.MessageList>
  );
}

function Message({ message }: { message: AgentMessage }) {
  return message.role === "user" ? (
    <sx.Text>{getMessageText(message)}</sx.Text>
  ) : (
    message.parts.map((part) => {
      const key = `${part.kind}-${part.id}`;
      return part.kind === "error" ? (
        <sx.ErrorChip key={key}>
          <sx.Text>{part.text}</sx.Text>
        </sx.ErrorChip>
      ) : part.kind === "event" ? (
        <sx.EventChip key={key}>
          <sx.Text color="textSecondary">{part.text}</sx.Text>
        </sx.EventChip>
      ) : part.kind === "text" ? (
        <MarkdownText key={key} text={part.text} />
      ) : null;
    })
  );
}
